import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { useApiClient } from "../api/client";
import { useI18n } from "../i18n/I18nProvider";
import { useMe } from "../hooks/useMe";
import { PageHeader } from "../components/PageHeader";

const EMBED_ORIGIN = "https://api.ai-assistant.amsolutions.tech";

export function IntegrationPage() {
  const api = useApiClient();
  const { t } = useI18n();

  const { me, loading } = useMe();

  // The "reveal flow" reproduces the original three-state widget-key panel:
  // 1. Masked display + a "Reveal key" button.
  // 2. Password prompt → confirms via /widget-key/reveal.
  // 3. Plain key + Copy / Hide buttons.
  const [state, setState] = useState<"masked" | "prompt" | "revealed">("masked");
  const [pwd, setPwd] = useState("");
  const [fullKey, setFullKey] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const masked = me?.widget_key_masked ?? "••••••••••••••••••••••••••••";

  async function onReveal(e: FormEvent) {
    e.preventDefault();
    if (!pwd) return;
    setSubmitting(true);
    try {
      const res = await api<{ widget_key: string }>("/tenant/api/widget-key/reveal", {
        method: "POST",
        body: { password: pwd },
      });
      setFullKey(res.widget_key);
      setState("revealed");
      setPwd("");
    } catch {
      toast.error(t("reveal_wrong"));
    } finally {
      setSubmitting(false);
    }
  }

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("copied"));
    } catch {
      toast.error(t("error_generic"));
    }
  }

  const snippet = me
    ? `<script>\n  window.CHATBOT_API_URL = "${EMBED_ORIGIN}";\n  window.CHATBOT_API_KEY = "${fullKey ?? me.widget_key_masked}";\n</script>\n<script src="${EMBED_ORIGIN}/static/widget.js" defer></script>`
    : "Chargement…";

  return (
    <div className="page is-active">
      <PageHeader titleKey="integration_title" subtitleKey="integration_subtitle" />

      <div className="settings-section">
        <h3>{t("widget_key_title")}</h3>
        <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 14 }}>
          {t("widget_key_desc")}
        </p>

        {state !== "revealed" && (
          <div className="key-display key-masked">{loading ? "…" : masked}</div>
        )}

        {state === "masked" && (
          <>
            <div className="alert alert-warn" style={{ margin: "10px 0" }}>
              {t("widget_key_warning")}
            </div>
            <div className="btn-row" style={{ marginTop: 14 }}>
              <button
                type="button"
                className="btn-sm btn-ghost"
                onClick={() => setState("prompt")}
              >
                {t("reveal_key")}
              </button>
            </div>
          </>
        )}

        {state === "prompt" && (
          <form onSubmit={onReveal} style={{ marginTop: 10 }}>
            <div className="field">
              <label>{t("reveal_password")}</label>
              <input
                type="password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                autoFocus
              />
            </div>
            <button type="submit" className="btn-sm btn-orange" disabled={submitting}>
              {t("reveal_btn")}
            </button>
            <button
              type="button"
              className="btn-sm btn-ghost"
              onClick={() => {
                setState("masked");
                setPwd("");
              }}
              style={{ marginLeft: 8 }}
            >
              {t("cancel")}
            </button>
          </form>
        )}

        {state === "revealed" && fullKey && (
          <>
            <div className="key-display">{fullKey}</div>
            <button
              type="button"
              className="btn-sm btn-ghost"
              onClick={() => copy(fullKey)}
            >
              {t("copy")}
            </button>
            <button
              type="button"
              className="btn-sm btn-danger"
              onClick={() => {
                setFullKey(null);
                setState("masked");
              }}
              style={{ marginLeft: 8 }}
            >
              {t("hide")}
            </button>
          </>
        )}
      </div>

      <div className="settings-section">
        <h3>{t("embed_code_title")}</h3>
        <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 10 }}>
          {t("embed_code_desc")}
        </p>
        <div className="code-block">{snippet}</div>
        <button
          type="button"
          className="btn-sm btn-ghost"
          onClick={() => copy(snippet)}
          disabled={!me}
        >
          {t("copy_snippet")}
        </button>
      </div>
    </div>
  );
}
